import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  NgZone,
  OnDestroy,
  Output,
  Renderer2
} from '@angular/core';

import { fromEvent, Subscription } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

@Directive({
  selector: '[appDragMirror]',
  standalone: true
})
export class DragMirrorDirective implements OnDestroy {
  @Output() dragStart = new EventEmitter<MouseEvent>();
  @Output() dragging  = new EventEmitter<MouseEvent>();
  @Output() dragEnd   = new EventEmitter<MouseEvent>();

  private mirrorEl?: HTMLElement;
  private subs?: Subscription;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) {}

  @HostListener('dragstart', ['$event'])
  onMouseDown(event: MouseEvent) {
    this.subs = new Subscription();
    // Only left‑click
    if (event.button !== 0) return;

    event.preventDefault();
    this.dragStart.emit(event);

    // Clone host into a mirror
    const original = this.elRef.nativeElement;
    this.mirrorEl = original.cloneNode(true) as HTMLElement;
    this.renderer.addClass(this.mirrorEl, 'drag-mirror');
    this.renderer.setStyle(this.mirrorEl, 'position', 'fixed');
    this.renderer.setStyle(this.mirrorEl, 'pointer-events', 'none');
    this.renderer.setStyle(this.mirrorEl, 'z-index', '10000');
    this.renderer.appendChild(document.body, this.mirrorEl);

    // Position it under the cursor immediately
    this.updatePosition(event.clientX, event.clientY);

    // Listen to move & up outside Angular to avoid excessive change‑detection
    this.ngZone.runOutsideAngular(() => {
      const move$ = fromEvent<MouseEvent>(document, 'mousemove').pipe(
        tap((moveEvent) => {
          this.updatePosition(moveEvent.clientX, moveEvent.clientY);
          this.ngZone.run(() => this.dragging.emit(moveEvent));
        })
      );

      const up$ = fromEvent<MouseEvent>(document, 'mouseup').pipe(
        tap(upEvent => {
          this.destroyMirror();
          this.ngZone.run(() => this.dragEnd.emit(upEvent));
        }),
        takeUntil(move$)
      );

      this.subs!.add(
        move$.pipe(
          switchMap(() => up$)
        ).subscribe()
      );
    });
  }

  private updatePosition(x: number, y: number) {
    if (!this.mirrorEl) return;
    // center the mirror on the pointer, or adjust offsets as needed
    this.renderer.setStyle(this.mirrorEl, 'top', `${y - 10}px`);
    this.renderer.setStyle(this.mirrorEl, 'left', `${x - 10}px`);
  }

  private destroyMirror() {
    if (this.mirrorEl && this.mirrorEl.parentNode) {
      this.renderer.removeChild(document.body, this.mirrorEl);
    }
    this.mirrorEl = undefined;
    this.subs?.unsubscribe();  // stop any lingering subscriptions
    this.subs = undefined;
  }

  ngOnDestroy() {
    this.destroyMirror();
  }
}
